/**
 * FractureApotheosisSkillEffect — Conjuration Tether path apotheosis.
 *
 * Full self-duplication. The player splits into three — themselves plus
 * two autonomous shades, each with an independent HP pool worth 40% of
 * the player's max HP. Shades fight autonomously for 15 seconds using
 * base-damage attacks (Phase 1 — no autonomous skill execution). When
 * the duration expires, surviving shades return their remaining HP to
 * the player as healing, then despawn. Shades destroyed in combat
 * return nothing — the investment is lost.
 *
 * Spawn geometry: 2 shades at 60° and 300° from the player's facing
 * direction, at a configurable radius (default 3 tiles × tileSize).
 *
 * Phase 1 note: the spec envisions full autonomous skill execution for
 * shades (independent cooldown tracking, skill AI). That complexity is
 * deferred. Phase 1 shades attack with raw damage only — no skill use.
 *
 * Upgrade hooks (resolver scope: 'fracture_apotheosis'):
 * - shadeDamageMultiplier (max): fraction of player base damage
 * - shadeHpFraction (max): fraction of player max HP per shade
 * - durationMs (max): apotheosis window duration
 *
 * Very long cooldown tier (4000ms). Extends ToggleSkillEffect for
 * active/cooldown lifecycle. Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Fracture Apotheosis
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';
import type { ShadeSummonConfig } from './TetherSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface FractureApotheosisDeps {
  readonly resolver: {
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
    getFacingDirection(): { x: number; y: number };
    getMaxHp(): number;
    getBaseDamage(): number;
    heal(amount: number): void;
  };
  readonly targeting: {
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: ShadeSummonConfig, x: number, y: number): string;
    destroy(instanceId: string): void;
    getShadeHp(instanceId: string): number;
    isShadeAlive(instanceId: string): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface FractureApotheosisConfig {
  readonly cooldownMs: number;
  /** Duration of the apotheosis window in ms. */
  readonly durationMs: number;
  /** Fraction of player's max HP each shade receives (0–1). */
  readonly shadeHpFraction: number;
  /** Fraction of player's base damage each shade deals (0–1). */
  readonly shadeDamageMultiplier: number;
  /** Shade movement speed (integration layer applies px/s). */
  readonly shadeSpeed: number;
  /** How much enemy aggro each shade draws (0–1). */
  readonly shadeAggroWeight: number;
  /** Leash radius in tiles for autonomous shades. */
  readonly shadeLeashRadiusTiles: number;
  /** Formation radius in tiles (distance from player to shade spawn). */
  readonly formationRadiusTiles: number;
  /** Tile size in pixels. */
  readonly tileSize: number;
}

const DEFAULT_CONFIG: FractureApotheosisConfig = {
  cooldownMs: 4000,              // very_long cooldown tier
  durationMs: 15000,             // 15-second apotheosis window
  shadeHpFraction: 0.4,          // 40% of player max HP per shade
  shadeDamageMultiplier: 0.75,   // 75% of player base damage (spec § Fracture Apotheosis)
  shadeSpeed: 1.0,
  shadeAggroWeight: 0.0,
  shadeLeashRadiusTiles: 8,
  formationRadiusTiles: 3,
  tileSize: 32,
};

/** Spawn angles relative to player facing: 60° and 300° (flanking). */
const SPAWN_ANGLE_OFFSETS = [
  Math.PI / 3,          // 60°
  (5 * Math.PI) / 3,    // 300°
];

/** Resolver scope for apotheosis-specific upgrades. */
const RESOLVER_SCOPE = 'fracture_apotheosis';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class FractureApotheosisSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'fracture_apotheosis';

  private readonly deps: FractureApotheosisDeps;
  private readonly config: FractureApotheosisConfig;

  // Active-state tracking
  private _durationRemaining = 0;
  private _shadeIds: string[] = [];
  private _activationCount = 0;
  private _returnedHp = 0;
  private _resolvedShadeHp = 0;
  private _resolvedDamage = 0;
  private _fizzled = false;
  private _lastSpawnPositions: Array<{ x: number; y: number }> = [];

  constructor(deps: FractureApotheosisDeps, config: Partial<FractureApotheosisConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get durationRemaining(): number { return this._durationRemaining; }
  get shadeIds(): readonly string[] { return this._shadeIds; }
  get shadeCount(): number { return this._shadeIds.length; }
  get activationCount(): number { return this._activationCount; }
  get returnedHp(): number { return this._returnedHp; }
  get resolvedShadeHp(): number { return this._resolvedShadeHp; }
  get resolvedDamage(): number { return this._resolvedDamage; }
  get fizzled(): boolean { return this._fizzled; }
  get lastSpawnPositions(): ReadonlyArray<{ x: number; y: number }> { return this._lastSpawnPositions; }

  // --- Integration layer hook ---

  /**
   * Called by the integration layer when an apotheosis shade is destroyed
   * in combat. No HP return — the investment is lost per spec.
   */
  onShadeDestroyed(shadeId: string): void {
    const idx = this._shadeIds.indexOf(shadeId);
    if (idx >= 0) {
      this._shadeIds.splice(idx, 1);
    }
  }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    return this.cooldownRemaining <= 0 && !this._isActive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle: apotheosis activation fails entirely — no cooldown penalty
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) return false;

    const pos = this.deps.player.getPosition();
    const facing = this.deps.player.getFacingDirection();
    const maxHp = this.deps.player.getMaxHp();
    const baseDamage = this.deps.player.getBaseDamage();

    // Resolve shade HP fraction (apotheosis scope, max aggregation)
    const hpFraction = this.deps.resolver.getMaxEffect(RESOLVER_SCOPE, 'shadeHpFraction')
      ?? this.config.shadeHpFraction;
    this._resolvedShadeHp = Math.floor(maxHp * hpFraction);

    // Resolve shade damage multiplier
    const damageMult = this.deps.resolver.getMaxEffect(RESOLVER_SCOPE, 'shadeDamageMultiplier')
      ?? this.config.shadeDamageMultiplier;
    this._resolvedDamage = Math.round(baseDamage * damageMult);

    // Resolve duration
    const durationMs = this.deps.resolver.getMaxEffect(RESOLVER_SCOPE, 'durationMs')
      ?? this.config.durationMs;

    // Compute player facing angle for spawn geometry
    const facingAngleRad = Math.atan2(facing.y, facing.x);
    const effectiveRadius = this.config.formationRadiusTiles * this.config.tileSize;

    this._lastSpawnPositions = [];
    this._shadeIds = [];
    this._returnedHp = 0;

    for (let i = 0; i < SPAWN_ANGLE_OFFSETS.length; i++) {
      const angle = facingAngleRad + SPAWN_ANGLE_OFFSETS[i];
      const spawnX = pos.x + Math.cos(angle) * effectiveRadius;
      const spawnY = pos.y + Math.sin(angle) * effectiveRadius;

      this._lastSpawnPositions.push({ x: spawnX, y: spawnY });

      const shadeConfig: ShadeSummonConfig = {
        id: `fracture_apo_shade_${this._activationCount}_${i}`,
        archetype: 'shade',
        baseHp: this._resolvedShadeHp,
        baseDamage: this._resolvedDamage,
        baseSpeed: this.config.shadeSpeed,
        leashRadiusTiles: this.config.shadeLeashRadiusTiles,
        aggroWeight: this.config.shadeAggroWeight,
        aiMode: 'autonomous-detached',
        shadeDamageMultiplier: damageMult,
        mirrorDistanceTiles: 0,      // not used in autonomous mode
        sharedHpPool: false,         // independent HP pools — core mechanic
      };

      const shadeId = this.deps.summons.spawn(shadeConfig, spawnX, spawnY);
      this._shadeIds.push(shadeId);
    }

    this._durationRemaining = durationMs;
    this._isActive = true;
    this._activationCount++;

    return true;
  }

  // --- ToggleSkillEffect lifecycle ---

  protected onDeactivate(): void {
    // Return surviving shade HP to player, then destroy
    for (const id of this._shadeIds) {
      if (this.deps.summons.isShadeAlive(id)) {
        const hp = this.deps.summons.getShadeHp(id);
        this._returnedHp += hp;
        this.deps.player.heal(hp);
      }
      this.deps.summons.destroy(id);
    }
    this._shadeIds = [];
    this._durationRemaining = 0;
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(delta: number): void {
    this._durationRemaining = Math.max(0, this._durationRemaining - delta);
    if (this._durationRemaining <= 0) {
      this.deactivate();
    }
  }

  destroy(): void {
    // Clean up any surviving shades
    for (const id of this._shadeIds) {
      this.deps.summons.destroy(id);
    }
    this._shadeIds = [];
    this._durationRemaining = 0;
    super.destroy();
  }
}
